import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import createEquipmentSelector from "store/equipment/selector";
import { FetchAllEquipment, getAllEquipments } from "store/equipment/actions";
import createJobSelector from "store/job/selector";
import { fetchAllJobs, getAllJobs } from "store/job/actions";
import createCustomerSelector from "store/customer/selector";
import { allCustomers, fetchAllCustomers } from "store/customer/actions";

import { IoGridOutline, IoSearchOutline, IoChevronDown, IoClose } from "react-icons/io5";
import { BiCctv } from "react-icons/bi";
import { FiBriefcase, FiUsers, FiMapPin, FiMap, FiChevronRight, FiTrash2 } from "react-icons/fi";
import { BsKeyboard, BsClockHistory } from "react-icons/bs";

const RECENT_STORAGE_KEY = "myspy_recent_searches_history";

const GlobalSearchBox = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    const role = userData?.role;

    // Redux selectors
    const equipmentSelector = useSelector(createEquipmentSelector);
    const { allEquipments } = equipmentSelector;

    const jobSelector = useSelector(createJobSelector);
    const { AllJobs } = jobSelector;

    const customerSelector = useSelector(createCustomerSelector);
    const { allCustomersData, backupAllCustomersData } = customerSelector;

    // Master dataset references so page filtering does not erase search data
    const masterEquipmentsRef = useRef([]);
    const masterJobsRef = useRef([]);
    const masterCustomersRef = useRef([]);

    useEffect(() => {
        if (allEquipments && allEquipments.length > masterEquipmentsRef.current.length) {
            masterEquipmentsRef.current = allEquipments;
        }
    }, [allEquipments]);

    useEffect(() => {
        if (AllJobs && AllJobs.length > masterJobsRef.current.length) {
            masterJobsRef.current = AllJobs;
        }
    }, [AllJobs]);

    useEffect(() => {
        const csList = backupAllCustomersData?.length ? backupAllCustomersData : allCustomersData;
        if (csList && csList.length > masterCustomersRef.current.length) {
            masterCustomersRef.current = csList;
        }
    }, [allCustomersData, backupAllCustomersData]);

    // Initial fetch if store is unpopulated
    useEffect(() => {
        if (!allEquipments || allEquipments.length === 0) {
            dispatch(FetchAllEquipment(true));
        }
        if (!AllJobs || AllJobs.length === 0) {
            dispatch(fetchAllJobs(true));
        }
        if (role !== "Technician" && (!allCustomersData || allCustomersData.length === 0)) {
            dispatch(fetchAllCustomers(true));
        }
    }, [dispatch, role]);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedTerm, setDebouncedTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("all"); // 'all' | 'equipment' | 'jobs' | 'customers' | 'addresses'
    const [isOpen, setIsOpen] = useState(false);

    // Debounce typing to eliminate any UI stutter or lag
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedTerm(searchTerm);
        }, 150);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Recent searches stored in localStorage (real searches only, no demo items)
    const [recentSearches, setRecentSearches] = useState(() => {
        try {
            const saved = localStorage.getItem(RECENT_STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    const saveRecentSearch = useCallback((item) => {
        try {
            setRecentSearches((prev) => {
                const filtered = prev.filter((r) => r.id !== item.id);
                const updated = [{
                    id: item.id,
                    type: item.type,
                    title: item.title,
                    subtitle: item.subtitle,
                    code: item.code || "",
                    fullAddress: item.fullAddress || "",
                    raw: item.raw || null,
                }, ...filtered].slice(0, 10);
                localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated));
                return updated;
            });
        } catch (e) {
            console.error(e);
        }
    }, []);

    const removeRecentSearch = useCallback((e, itemId) => {
        e.stopPropagation();
        setRecentSearches((prev) => {
            const updated = prev.filter((r) => r.id !== itemId);
            localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });
    }, []);

    const clearAllRecentSearches = useCallback((e) => {
        e.stopPropagation();
        setRecentSearches([]);
        localStorage.removeItem(RECENT_STORAGE_KEY);
    }, []);

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Global keyboard shortcut: Ctrl+K / Cmd+K and Esc
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setIsOpen((prev) => {
                    const next = !prev;
                    if (next) {
                        setTimeout(() => inputRef.current?.focus(), 50);
                    }
                    return next;
                });
                return;
            }

            if (e.key === "Escape") {
                setIsOpen(false);
                return;
            }

            if (isOpen && document.activeElement !== inputRef.current) {
                const key = e.key.toLowerCase();
                if (key === "e") {
                    e.preventDefault();
                    setActiveCategory("equipment");
                } else if (key === "j") {
                    e.preventDefault();
                    setActiveCategory("jobs");
                } else if (key === "c" && role !== "Technician") {
                    e.preventDefault();
                    setActiveCategory("customers");
                } else if (key === "a") {
                    e.preventDefault();
                    setActiveCategory("addresses");
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, role]);

    // Pre-indexed items with single lowercase search keys for high-speed matching
    const indexedEquipments = useMemo(() => {
        const source = masterEquipmentsRef.current?.length ? masterEquipmentsRef.current : (allEquipments || []);
        return source.map((eq) => {
            const title = eq.equipmentType || eq.type || "Equipment";
            const subtitle = eq.details?.serial_number || eq.status || "";
            const code = eq.details?.number ? String(eq.details.number) : (eq.details?.serial_number || "");
            const searchKey = `${title} ${subtitle} ${code} ${eq.type || ""} ${eq.status || ""}`.toLowerCase();
            return {
                id: `eq-${eq._id || Math.random()}`,
                type: "equipment",
                title,
                subtitle,
                code,
                searchKey,
                raw: eq,
            };
        });
    }, [allEquipments]);

    const indexedJobs = useMemo(() => {
        const source = masterJobsRef.current?.length ? masterJobsRef.current : (AllJobs || []);
        const jobTypeLabels = {
            InstallationBossUnit: "Installation Boss Unit",
            InstallationTimeLapse: "Installation Timelapse",
            ChangeOfEquipment: "Change Of Equipment",
            InstallationXseries: "Installation (X-Series)",
            RemovalOrder: "Removal Order",
            Relocation: "Relocation",
            Services: "Service Request",
        };
        return source.map((job) => {
            const title = jobTypeLabels[job.jobType] || job.jobType || (job.jobId ? `Job #${job.jobId}` : "Job");
            const subtitle = job.site_address || job.status || "";
            const code = job.jobId ? String(job.jobId) : "";
            const searchKey = `${title} ${subtitle} ${code} ${job.jobType || ""} ${job.site_address || ""} ${job.customerName || ""}`.toLowerCase();
            return {
                id: `job-${job._id || job.jobId || Math.random()}`,
                type: "job",
                title,
                subtitle,
                code,
                searchKey,
                raw: job,
            };
        });
    }, [AllJobs]);

    const indexedCustomers = useMemo(() => {
        if (role === "Technician") return [];
        const source = masterCustomersRef.current?.length
            ? masterCustomersRef.current
            : (backupAllCustomersData?.length ? backupAllCustomersData : (allCustomersData || []));
        return source.map((c) => {
            const title = c.name || c.contactName || "Customer";
            const subtitle = c.contactName && c.name !== c.contactName ? c.contactName : (c.email || c.phone_number || c.address || "");
            const code = c.customerId ? String(c.customerId) : "";
            const searchKey = `${title} ${subtitle} ${code} ${c.email || ""} ${c.phone_number || ""} ${c.address || ""}`.toLowerCase();
            return {
                id: `cust-${c._id || Math.random()}`,
                type: "customer",
                title,
                subtitle,
                code,
                searchKey,
                raw: c,
            };
        });
    }, [allCustomersData, backupAllCustomersData, role]);

    const indexedAddresses = useMemo(() => {
        const map = new Map();
        const jSource = masterJobsRef.current?.length ? masterJobsRef.current : (AllJobs || []);
        jSource.forEach((job) => {
            const addr = job.site_address?.trim();
            if (addr && addr.length > 2) {
                const key = addr.toLowerCase();
                if (!map.has(key)) {
                    const subParts = [job.suburb, job.city, job.state].filter(Boolean).join(", ");
                    const subtitle = subParts || (job.customerName ? `Job for ${job.customerName}` : (job.jobId ? `Job #${job.jobId}` : "Site Address"));
                    const fullAddress = subParts ? `${addr}, ${subParts}` : addr;
                    const searchKey = `${addr} ${subParts} ${subtitle}`.toLowerCase();
                    map.set(key, {
                        id: `addr-job-${job._id || job.jobId || Math.random()}`,
                        type: "address",
                        title: addr,
                        subtitle,
                        fullAddress,
                        searchKey,
                        rawJob: job,
                    });
                }
            }
        });

        if (role !== "Technician") {
            const cSource = masterCustomersRef.current?.length
                ? masterCustomersRef.current
                : (backupAllCustomersData?.length ? backupAllCustomersData : (allCustomersData || []));
            cSource.forEach((c) => {
                const addr = c.address?.trim();
                if (addr && addr.length > 2) {
                    const key = addr.toLowerCase();
                    if (!map.has(key)) {
                        const subtitle = c.name ? `Customer: ${c.name}` : "Customer Address";
                        const searchKey = `${addr} ${subtitle}`.toLowerCase();
                        map.set(key, {
                            id: `addr-cust-${c._id || Math.random()}`,
                            type: "address",
                            title: addr,
                            subtitle,
                            fullAddress: addr,
                            searchKey,
                            rawCustomer: c,
                        });
                    }
                }
            });
        }
        return Array.from(map.values());
    }, [AllJobs, allCustomersData, backupAllCustomersData, role]);

    // Ultra-fast filtering against lowercase search query
    const query = debouncedTerm.trim().toLowerCase();

    const filteredEquipments = useMemo(() => {
        if (!query) return indexedEquipments.slice(0, 10);
        return indexedEquipments.filter((eq) => eq.searchKey.includes(query)).slice(0, 15);
    }, [indexedEquipments, query]);

    const filteredJobs = useMemo(() => {
        if (!query) return indexedJobs.slice(0, 10);
        return indexedJobs.filter((j) => j.searchKey.includes(query)).slice(0, 15);
    }, [indexedJobs, query]);

    const filteredCustomers = useMemo(() => {
        if (role === "Technician") return [];
        if (!query) return indexedCustomers.slice(0, 10);
        return indexedCustomers.filter((c) => c.searchKey.includes(query)).slice(0, 15);
    }, [indexedCustomers, query, role]);

    const filteredAddresses = useMemo(() => {
        if (!query) return indexedAddresses.slice(0, 10);
        return indexedAddresses.filter((a) => a.searchKey.includes(query)).slice(0, 15);
    }, [indexedAddresses, query]);

    // Active results display: If query is empty, show ONLY recent searches!
    const displayResults = useMemo(() => {
        if (!query) {
            if (activeCategory === "all") {
                return recentSearches;
            }
            return recentSearches.filter((item) => item.type === activeCategory);
        }

        if (activeCategory === "equipment") return filteredEquipments;
        if (activeCategory === "jobs") return filteredJobs;
        if (activeCategory === "customers") return filteredCustomers;
        if (activeCategory === "addresses") return filteredAddresses;

        // 'all' category: balanced sample from each category
        const combined = [];
        const limitPerCat = 4;
        combined.push(...filteredAddresses.slice(0, limitPerCat));
        combined.push(...filteredEquipments.slice(0, limitPerCat));
        combined.push(...filteredJobs.slice(0, limitPerCat));
        combined.push(...filteredCustomers.slice(0, limitPerCat));
        return combined;
    }, [
        recentSearches,
        activeCategory,
        filteredAddresses,
        filteredEquipments,
        filteredJobs,
        filteredCustomers,
        query,
    ]);

    const allCount = filteredAddresses.length + filteredEquipments.length + filteredJobs.length + filteredCustomers.length;

    // Item click handler
    const handleItemClick = (item) => {
        saveRecentSearch(item);
        setIsOpen(false);

        if (item.type === "equipment") {
            if (history.location.pathname !== "/new-equipment") {
                history.push("/new-equipment");
            }
            if (item.raw) dispatch(getAllEquipments([item.raw]));
        } else if (item.type === "job") {
            if (history.location.pathname !== "/jobs") {
                history.push("/jobs");
            }
            if (item.raw) dispatch(getAllJobs([item.raw]));
        } else if (item.type === "customer") {
            if (history.location.pathname !== "/customers") {
                history.push("/customers");
            }
            if (item.raw) dispatch(allCustomers([item.raw]));
        } else if (item.type === "address") {
            if (item.rawJob) {
                if (history.location.pathname !== "/jobs") {
                    history.push("/jobs");
                }
                const matching = (masterJobsRef.current || []).filter(
                    (j) => j.site_address?.toLowerCase() === item.title.toLowerCase()
                );
                dispatch(getAllJobs(matching.length > 0 ? matching : [item.rawJob]));
            } else if (item.rawCustomer) {
                if (history.location.pathname !== "/customers") {
                    history.push("/customers");
                }
                const matching = (masterCustomersRef.current || []).filter(
                    (c) => c.address?.toLowerCase() === item.title.toLowerCase()
                );
                dispatch(allCustomers(matching.length > 0 ? matching : [item.rawCustomer]));
            } else {
                if (history.location.pathname !== "/jobs") {
                    history.push("/jobs");
                }
            }
        }
    };

    const sidebarCategories = [
        { id: "all", label: "Search All", icon: <IoGridOutline fontSize={18} />, badge: "Ctrl K" },
        { id: "equipment", label: "Equipment", icon: <BiCctv fontSize={19} />, badge: "E" },
        { id: "jobs", label: "Jobs", icon: <FiBriefcase fontSize={18} />, badge: "J" },
        ...(role !== "Technician" ? [{ id: "customers", label: "Customers", icon: <FiUsers fontSize={18} />, badge: "C" }] : []),
        { id: "addresses", label: "Addresses", icon: <FiMapPin fontSize={18} />, badge: "A" },
    ];

    const categoryLabels = {
        all: "Search All",
        equipment: "Equipment",
        jobs: "Jobs",
        customers: "Customers",
        addresses: "Addresses",
    };

    const isRecentMode = !query;

    return (
        <div ref={containerRef} style={{ position: "relative", width: "100%", maxWidth: "560px", margin: "0 auto" }}>
            {/* Header Search Input Bar */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#ffffff",
                    border: isOpen ? "1px solid #0d6efd" : "1px solid #d0d5dd",
                    borderRadius: "10px",
                    height: "40px",
                    boxShadow: isOpen ? "0 0 0 3px rgba(13, 110, 253, 0.15)" : "0 1px 2px rgba(16, 24, 40, 0.05)",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
            >
                {/* Left Category Selector Toggle */}
                <div
                    onClick={() => setIsOpen(true)}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "0 12px 0 14px",
                        cursor: "pointer",
                        color: "#1f2937",
                        fontSize: "14px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        userSelect: "none",
                    }}
                >
                    <IoGridOutline fontSize={17} color="#374151" />
                    <span>{categoryLabels[activeCategory] || "Search All"}</span>
                    <IoChevronDown fontSize={14} color="#6b7280" />
                </div>

                {/* Vertical Divider */}
                <div style={{ height: "22px", width: "1px", backgroundColor: "#e5e7eb", flexShrink: 0 }} />

                {/* Search Icon */}
                <IoSearchOutline fontSize={18} color="#6b7280" style={{ marginLeft: "12px", flexShrink: 0 }} />

                {/* Search Input */}
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    placeholder="Search equipment, jobs, customers, or addresses..."
                    onFocus={() => setIsOpen(true)}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && displayResults.length > 0) {
                            handleItemClick(displayResults[0]);
                        }
                    }}
                    style={{
                        border: "none",
                        outline: "none",
                        padding: "0 10px",
                        fontSize: "14px",
                        color: "#111827",
                        width: "100%",
                        backgroundColor: "transparent",
                    }}
                />

                {/* Clear Input Button when typing */}
                {searchTerm && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearchTerm("");
                            setDebouncedTerm("");
                            inputRef.current?.focus();
                        }}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#9ca3af",
                            cursor: "pointer",
                            padding: "0 6px",
                            display: "flex",
                            alignItems: "center",
                        }}
                        title="Clear input"
                    >
                        <IoClose fontSize={16} />
                    </button>
                )}

                {/* Shortcut Pill */}
                <span
                    onClick={() => {
                        setIsOpen(true);
                        inputRef.current?.focus();
                    }}
                    style={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#6b7280",
                        backgroundColor: "#f3f4f6",
                        borderRadius: "6px",
                        border: "1px solid #e5e7eb",
                        marginRight: "12px",
                        letterSpacing: "0.5px",
                        flexShrink: 0,
                        cursor: "pointer",
                        userSelect: "none",
                    }}
                >
                    Ctrl K
                </span>
            </div>

            {/* Floating Dropdown Modal / Popover (Centered directly below the input) */}
            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 99999,
                        width: "800px",
                        maxWidth: "94vw",
                        backgroundColor: "#ffffff",
                        borderRadius: "16px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 20px 45px -10px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.04)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Top Notch Pointer */}
                    <div
                        style={{
                            position: "absolute",
                            top: "-6px",
                            left: "50px",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#ffffff",
                            borderLeft: "1px solid #e5e7eb",
                            borderTop: "1px solid #e5e7eb",
                            transform: "rotate(45deg)",
                            zIndex: 1,
                        }}
                    />

                    {/* Main Content Area */}
                    <div style={{ display: "flex", minHeight: "340px", maxHeight: "490px" }}>
                        {/* Left Sidebar (Categories) */}
                        <div
                            style={{
                                width: "210px",
                                borderRight: "1px solid #f1f3f5",
                                padding: "16px 10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                backgroundColor: "#fafbfc",
                                flexShrink: 0,
                            }}
                        >
                            {sidebarCategories.map((cat) => {
                                const isActive = activeCategory === cat.id;
                                return (
                                    <div
                                        key={cat.id}
                                        onClick={() => setActiveCategory(cat.id)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "9px 12px",
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            backgroundColor: isActive ? "#eaf2ff" : "transparent",
                                            color: isActive ? "#0d6efd" : "#495057",
                                            fontWeight: isActive ? 600 : 500,
                                            transition: "all 0.12s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) e.currentTarget.style.backgroundColor = "#f1f3f5";
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <span style={{ display: "flex", color: isActive ? "#0d6efd" : "#6c757d" }}>
                                                {cat.icon}
                                            </span>
                                            <span style={{ fontSize: "14px" }}>{cat.label}</span>
                                        </div>
                                        <span
                                            style={{
                                                padding: "2px 7px",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                borderRadius: "6px",
                                                backgroundColor: isActive ? "#d3e4ff" : "#eaedf1",
                                                color: isActive ? "#0d6efd" : "#868e96",
                                            }}
                                        >
                                            {cat.badge}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Right Area */}
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                padding: "16px 18px",
                                overflow: "hidden",
                            }}
                        >
                            {/* Header row: Recent Searches Header (if empty query) OR Filter Pills (when searching) */}
                            {isRecentMode ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginBottom: "12px",
                                        paddingBottom: "8px",
                                        borderBottom: "1px solid #f1f3f5",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, color: "#4b5563", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                        <BsClockHistory color="#6b7280" />
                                        <span>{activeCategory === "all" ? "Recent Searches" : `Recent ${categoryLabels[activeCategory] || "Searches"}`}</span>
                                    </div>
                                    {displayResults.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={clearAllRecentSearches}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#dc3545",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                padding: "3px 8px",
                                                borderRadius: "6px",
                                                transition: "background-color 0.15s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fee2e2";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <FiTrash2 fontSize={12} /> Clear all
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "14px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategory("all")}
                                        style={{
                                            backgroundColor: activeCategory === "all" ? "#0d6efd" : "#f1f3f5",
                                            color: activeCategory === "all" ? "#ffffff" : "#495057",
                                            padding: "5px 14px",
                                            borderRadius: "20px",
                                            fontWeight: activeCategory === "all" ? 600 : 500,
                                            fontSize: "13px",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        All ({allCount})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategory("equipment")}
                                        style={{
                                            backgroundColor: activeCategory === "equipment" ? "#0d6efd" : "#f1f3f5",
                                            color: activeCategory === "equipment" ? "#ffffff" : "#495057",
                                            padding: "5px 14px",
                                            borderRadius: "20px",
                                            fontWeight: activeCategory === "equipment" ? 600 : 500,
                                            fontSize: "13px",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Equipment ({filteredEquipments.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategory("jobs")}
                                        style={{
                                            backgroundColor: activeCategory === "jobs" ? "#0d6efd" : "#f1f3f5",
                                            color: activeCategory === "jobs" ? "#ffffff" : "#495057",
                                            padding: "5px 14px",
                                            borderRadius: "20px",
                                            fontWeight: activeCategory === "jobs" ? 600 : 500,
                                            fontSize: "13px",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Jobs ({filteredJobs.length})
                                    </button>
                                    {role !== "Technician" && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveCategory("customers")}
                                            style={{
                                                backgroundColor: activeCategory === "customers" ? "#0d6efd" : "#f1f3f5",
                                                color: activeCategory === "customers" ? "#ffffff" : "#495057",
                                                padding: "5px 14px",
                                                borderRadius: "20px",
                                                fontWeight: activeCategory === "customers" ? 600 : 500,
                                                fontSize: "13px",
                                                border: "none",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Customers ({filteredCustomers.length})
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategory("addresses")}
                                        style={{
                                            backgroundColor: activeCategory === "addresses" ? "#0d6efd" : "#f1f3f5",
                                            color: activeCategory === "addresses" ? "#ffffff" : "#495057",
                                            padding: "5px 14px",
                                            borderRadius: "20px",
                                            fontWeight: activeCategory === "addresses" ? 600 : 500,
                                            fontSize: "13px",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Addresses ({filteredAddresses.length})
                                    </button>
                                </div>
                            )}

                            {/* Results / Recent Searches List */}
                            <div
                                style={{
                                    flex: 1,
                                    overflowY: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "3px",
                                    paddingRight: "4px",
                                }}
                            >
                                {displayResults.length === 0 ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "45px 0",
                                            color: "#6c757d",
                                            textAlign: "center",
                                        }}
                                    >
                                        {isRecentMode ? (
                                            <BsClockHistory fontSize={34} color="#adb5bd" />
                                        ) : (
                                            <IoSearchOutline fontSize={36} color="#adb5bd" />
                                        )}
                                        <div style={{ marginTop: "10px", fontSize: "15px", fontWeight: 500 }}>
                                            {isRecentMode ? "No recent searches" : `No results found for "${searchTerm}"`}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                                            {isRecentMode
                                                ? "Search for equipment, jobs, customers, or addresses to see them here"
                                                : "Check the spelling or try searching another keyword"}
                                        </div>
                                    </div>
                                ) : (
                                    displayResults.map((item) => {
                                        return (
                                            <div
                                                key={item.id}
                                                onClick={() => handleItemClick(item)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "10px 14px",
                                                    borderRadius: "10px",
                                                    cursor: "pointer",
                                                    transition: "background-color 0.12s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#f8fafc";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = "transparent";
                                                }}
                                            >
                                                {/* Left Icon and Details */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "14px",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            flexShrink: 0,
                                                            color: "#374151",
                                                        }}
                                                    >
                                                        {item.type === "address" && <FiMapPin fontSize={20} />}
                                                        {item.type === "equipment" && <BiCctv fontSize={22} />}
                                                        {item.type === "job" && <FiBriefcase fontSize={19} />}
                                                        {item.type === "customer" && <FiUsers fontSize={20} />}
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            minWidth: 0,
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize: "14px",
                                                                fontWeight: 600,
                                                                color: "#1f2937",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {item.title}
                                                        </div>
                                                        {item.subtitle && (
                                                            <div
                                                                style={{
                                                                    fontSize: "12px",
                                                                    color: "#6b7280",
                                                                    marginTop: "2px",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                {item.subtitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Right Badges & Actions */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        flexShrink: 0,
                                                        marginLeft: "12px",
                                                    }}
                                                >
                                                    {item.type === "address" && (
                                                        <>
                                                            <span
                                                                style={{
                                                                    padding: "3px 10px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    borderRadius: "12px",
                                                                    backgroundColor: "#dcfce7",
                                                                    color: "#15803d",
                                                                }}
                                                            >
                                                                Address
                                                            </span>
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.open(
                                                                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                                            item.fullAddress || item.title
                                                                        )}`,
                                                                        "_blank"
                                                                    );
                                                                }}
                                                                title="Open in Google Maps"
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: "4px",
                                                                    color: "#0d6efd",
                                                                    fontSize: "12px",
                                                                    fontWeight: 500,
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                <FiMap fontSize={14} /> View on Map
                                                            </span>
                                                        </>
                                                    )}

                                                    {item.type === "equipment" && (
                                                        <>
                                                            <span
                                                                style={{
                                                                    padding: "3px 10px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    borderRadius: "12px",
                                                                    backgroundColor: "#e0edff",
                                                                    color: "#0d6efd",
                                                                }}
                                                            >
                                                                Equipment
                                                            </span>
                                                            {item.code && (
                                                                <span style={{ fontSize: "12px", color: "#4b5563", fontWeight: 500 }}>
                                                                    {item.code}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}

                                                    {item.type === "job" && (
                                                        <>
                                                            <span
                                                                style={{
                                                                    padding: "3px 10px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    borderRadius: "12px",
                                                                    backgroundColor: "#f3e8ff",
                                                                    color: "#7c3aed",
                                                                }}
                                                            >
                                                                Job
                                                            </span>
                                                            {item.code && (
                                                                <span style={{ fontSize: "12px", color: "#4b5563", fontWeight: 500 }}>
                                                                    {item.code}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}

                                                    {item.type === "customer" && (
                                                        <>
                                                            <span
                                                                style={{
                                                                    padding: "3px 10px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    borderRadius: "12px",
                                                                    backgroundColor: "#fef3c7",
                                                                    color: "#b45309",
                                                                }}
                                                            >
                                                                Customer
                                                            </span>
                                                            {item.code && (
                                                                <span style={{ fontSize: "12px", color: "#4b5563", fontWeight: 500 }}>
                                                                    {item.code}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* In Recent Mode: Clean individual remove button */}
                                                    {isRecentMode ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => removeRecentSearch(e, item.id)}
                                                            title="Remove from recent searches"
                                                            style={{
                                                                border: "1px solid transparent",
                                                                background: "#f3f4f6",
                                                                color: "#6b7280",
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                transition: "all 0.15s ease",
                                                                flexShrink: 0,
                                                                marginLeft: "6px",
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.color = "#ffffff";
                                                                e.currentTarget.style.backgroundColor = "#ef4444";
                                                                e.currentTarget.style.borderColor = "#ef4444";
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.color = "#6b7280";
                                                                e.currentTarget.style.backgroundColor = "#f3f4f6";
                                                                e.currentTarget.style.borderColor = "transparent";
                                                            }}
                                                        >
                                                            <IoClose fontSize={14} />
                                                        </button>
                                                    ) : (
                                                        <FiChevronRight fontSize={16} color="#9ca3af" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer: Clean shortcut indicators, no redundant Recent Searches button */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "9px 18px",
                            borderTop: "1px solid #f1f3f5",
                            backgroundColor: "#fafbfc",
                            fontSize: "12px",
                            color: "#6b7280",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                <kbd style={{ padding: "2px 6px", backgroundColor: "#eef2f6", borderRadius: "4px", border: "1px solid #d0d7de", fontSize: "11px", fontWeight: 600, color: "#374151" }}>↵</kbd>
                                <span>to select</span>
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                <kbd style={{ padding: "2px 6px", backgroundColor: "#eef2f6", borderRadius: "4px", border: "1px solid #d0d7de", fontSize: "11px", fontWeight: 600, color: "#374151" }}>Esc</kbd>
                                <span>to close</span>
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <BsKeyboard fontSize={15} color="#9ca3af" />
                            <span><kbd style={{ padding: "2px 6px", backgroundColor: "#eef2f6", borderRadius: "4px", border: "1px solid #d0d7de", fontSize: "11px", fontWeight: 600, color: "#374151" }}>Ctrl K</kbd> to search</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalSearchBox;